/**
 * Gestión de inactividad de sesión (cliente).
 * La fuente de verdad de vigencia sigue siendo el backend (sessions).
 */

export const SESSION_CHANNEL = 'pdfsigner-session'
export const STORAGE_LAST_ACTIVITY = 'pdfsigner_last_activity_at'
export const STORAGE_SESSION_META = 'pdfsigner_session_meta'
export const EVENT_SESSION_WARNING = 'pdfsigner:session-warning'
export const EVENT_SESSION_EXPIRED = 'pdfsigner:session-expired'
export const EVENT_SESSION_LOGOUT = 'pdfsigner:session-logout'
export const EVENT_AUTH_CLEARED = 'pdfsigner:auth-cleared'

export const INACTIVITY_MS = 20 * 60 * 1000
export const WARNING_MS = 18 * 60 * 1000
export const ACTIVITY_THROTTLE_MS = 60 * 1000

const HUMAN_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'mousedown']

export function getAuthToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token')
}

export function getSessionMeta() {
  try {
    const raw = localStorage.getItem(STORAGE_SESSION_META) || sessionStorage.getItem(STORAGE_SESSION_META)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function saveSessionMeta(meta, rememberMe) {
  const payload = JSON.stringify(meta || {})
  localStorage.removeItem(STORAGE_SESSION_META)
  sessionStorage.removeItem(STORAGE_SESSION_META)
  if (rememberMe) localStorage.setItem(STORAGE_SESSION_META, payload)
  else sessionStorage.setItem(STORAGE_SESSION_META, payload)
}

export function clearSessionMeta() {
  localStorage.removeItem(STORAGE_SESSION_META)
  sessionStorage.removeItem(STORAGE_SESSION_META)
  localStorage.removeItem(STORAGE_LAST_ACTIVITY)
  sessionStorage.removeItem(STORAGE_LAST_ACTIVITY)
}

export function markLocalActivity(at = Date.now()) {
  const value = String(at)
  localStorage.setItem(STORAGE_LAST_ACTIVITY, value)
  return at
}

export function readLastActivity() {
  const raw = localStorage.getItem(STORAGE_LAST_ACTIVITY)
  const n = raw ? Number(raw) : 0
  return Number.isFinite(n) ? n : 0
}

export function isEditorRoute(pathname = window.location.pathname) {
  return typeof pathname === 'string' && pathname.startsWith('/editor')
}

export function isShareModalOpen() {
  try {
    return sessionStorage.getItem('pdfEditor_showShareModal') === 'true'
  } catch {
    return false
  }
}

/**
 * ¿Se puede redirigir a login sin riesgo alto de pérdida silenciosa?
 * En /editor con modal o trabajo potencial: no redirigir automáticamente.
 */
export function shouldDeferHardRedirect(pathname = window.location.pathname) {
  if (isShareModalOpen()) return true
  if (isEditorRoute(pathname)) return true
  return false
}

export function broadcastSession(type, payload = {}) {
  try {
    const channel = new BroadcastChannel(SESSION_CHANNEL)
    channel.postMessage({ type, at: Date.now(), ...payload })
    channel.close()
  } catch {
    // Fallback: storage event
    localStorage.setItem('pdfsigner_session_broadcast', JSON.stringify({ type, at: Date.now(), ...payload }))
  }
}

export function createIdleMonitor({
  onHumanActivity,
  onWarning,
  onExpired,
  inactivityMs = INACTIVITY_MS,
  warningMs = WARNING_MS,
  nowFn = () => Date.now(),
} = {}) {
  let warned = false
  let expired = false
  let timer = null
  let lastSentActivity = 0
  let stopped = false

  const evaluate = () => {
    if (stopped || expired) return
    const last = readLastActivity() || nowFn()
    const idleFor = nowFn() - last
    if (idleFor >= inactivityMs) {
      expired = true
      onExpired?.({ idleFor })
      return
    }
    if (!warned && idleFor >= warningMs) {
      warned = true
      onWarning?.({ idleFor, remainingMs: inactivityMs - idleFor })
    }
    if (idleFor < warningMs) warned = false
  }

  const handleHuman = () => {
    if (stopped || expired) return
    const at = markLocalActivity(nowFn())
    warned = false
    const shouldPing = at - lastSentActivity >= ACTIVITY_THROTTLE_MS
    if (shouldPing) {
      lastSentActivity = at
      onHumanActivity?.({ at })
    }
    broadcastSession('activity', { at })
  }

  const start = () => {
    stopped = false
    expired = false
    warned = false
    markLocalActivity(nowFn())
    HUMAN_EVENTS.forEach((evt) => window.addEventListener(evt, handleHuman, { passive: true }))
    timer = window.setInterval(evaluate, 1000)
    evaluate()
  }

  const stop = () => {
    stopped = true
    if (timer) window.clearInterval(timer)
    timer = null
    HUMAN_EVENTS.forEach((evt) => window.removeEventListener(evt, handleHuman))
  }

  const resetAfterContinue = () => {
    warned = false
    expired = false
    markLocalActivity(nowFn())
  }

  return { start, stop, evaluate, handleHuman, resetAfterContinue }
}
